import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(() => this.#prefetch());
  }

  #prefetch = async () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.get(url)) return;
    const html = await this.fetchProductPage(url);
    if (!html) return;
    const content = this.#buildQuickAddContent(html);
    if (content) this.#cachedContent.set(url, content);
  };

  /**
   * Builds the quick-add modal content from a fetched product page. For the Oddit
   * buy box, prepends up to two product images as a left media column so the modal
   * matches the live two-up layout (QA: ALY-64).
   * @param {Document} html
   * @returns {Element | null}
   */
  #buildQuickAddContent(html) {
    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) return null;
    const grid = /** @type {Element} */ (gridElement.cloneNode(true));
    if (!grid.matches?.('[data-oddit-quick-add]')) return grid;

    const combined = document.createElement('div');
    combined.setAttribute('data-oddit-quick-add', '');
    combined.className = 'oddit-section-main-product__qa-grid';

    const imgs = html.querySelectorAll('.oddit-section-main-product__slide-img');
    if (imgs.length) {
      const media = document.createElement('div');
      media.className = 'oddit-section-main-product__qa-media';
      for (let i = 0; i < imgs.length; i++) {
        const img = /** @type {Element} */ (imgs[i].cloneNode(true));
        img.removeAttribute('loading');
        img.removeAttribute('sizes');
        img.removeAttribute('srcset');
        media.appendChild(img);
      }
      combined.appendChild(media);
    }
    combined.appendChild(grid);
    return combined;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const content = this.#buildQuickAddContent(html);
        if (content) {
          this.#cachedContent.set(currentUrl, content);
          productGrid = content;
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#openQuickAddModal();
    } else if (currentUrl) {
      window.location.href = currentUrl;
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Oddit custom buy box (gallery + supply selector + add-to-cart): render it
    // as-is and let the Oddit JS initialise it, skipping Horizon's standard
    // variant-picker reflow which doesn't apply to it (QA: ALY-64).
    if (productGrid.matches?.('[data-oddit-quick-add]') || productGrid.querySelector?.('[data-oddit-quick-add]')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'oddit-section-main-product is-visible';
      wrapper.appendChild(productGrid);
      modalContent.replaceChildren(wrapper);
      // The modal content is a 7-column grid for Horizon's standard layout, which
      // crushes our single buy-box into one narrow column. Make it a plain block
      // so the buy box spans the full width (QA: ALY-64).
      modalContent.style.display = 'block';
      // The cloned content is never seen by the scroll-reveal observer, so its
      // `.oddit-reveal` elements would stay at opacity:0 (blank modal) — force them visible.
      wrapper.querySelectorAll('.oddit-reveal').forEach((el) => el.classList.add('is-visible'));
      // Default to one-time in the modal — matches the live quick-add ("Add to
      // cart" at the one-time price, no subscribe/one-time toggle).
      const oddBuyBox = productGrid.matches?.('[data-buy-box]') ? productGrid : productGrid.querySelector?.('[data-buy-box]');
      if (oddBuyBox) {
        oddBuyBox.setAttribute('data-default-mode', 'onetime');
        // Remove the subscribe/one-time block entirely so the modal adds one-time
        // at the one-time price (the hidden "subscribe" radio would otherwise win
        // in syncFromInputs and force the subscribe price) — matches live.
        const purchase = oddBuyBox.querySelector('[data-purchase-options]');
        if (purchase) purchase.remove();
      }
      if (window.OdditMainProduct && typeof window.OdditMainProduct.init === 'function') {
        window.OdditMainProduct.init(wrapper);
      }
      // Only the image column scrolls — cap its height to the buy box so the buy
      // box stays put and just the images scroll (QA: ALY-64).
      const media = wrapper.querySelector('.oddit-section-main-product__qa-media');
      if (media && oddBuyBox && productGrid.matches?.('.oddit-section-main-product__qa-grid')) {
        requestAnimationFrame(() => {
          // Pin the grid to the buy box's natural height. The buy box is
          // align-self:start (its own height); the media column is
          // align-self:stretch, so it fills this fixed height and overflows —
          // only the images scroll, the buy box stays put (QA: ALY-64).
          // Desktop two-up layout only; on mobile the columns stack and the
          // whole modal scrolls, so leave the grid height auto.
          if (window.matchMedia('(min-width: 750px)').matches) {
            productGrid.style.height = oddBuyBox.offsetHeight + 'px';
            media.style.overflowY = 'auto';
          } else {
            productGrid.style.height = '';
            media.style.overflowY = '';
          }
        });
      }
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // The Oddit cart drawer adds via its own flow and fires `cart:updated`
    // (then opens itself). Close this dialog on that event too (QA: ALY-64).
    document.addEventListener('cart:updated', this.#handleOdditCartUpdate, { signal: this.#abortController.signal });
  }

  #handleOdditCartUpdate = () => {
    this.closeDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
